import { CheckCircle2, CircleAlert, Clock3, FileCheck2, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { DataTable } from "../components/DataTable";
import { Badge, Button, Card, EmptyState, Skeleton } from "../components/ui";
import { useTelemedicine } from "../data/platform-data";
import { subscribeDemoReset } from "../lib/demo-session";
import { env } from "../lib/env";
import {
  clearTelemedicineDemoSession,
  readTelemedicineDemoSession,
  writeTelemedicineDemoSession,
  type TelemedicineDemoRecord,
} from "../lib/telemedicine-demo-session";

export function operationalState(
  status: string,
  firstAccess: string,
  demo: Partial<TelemedicineDemoRecord> = {},
) {
  if (status === "ATIVO" && firstAccess === "Concluído")
    return {
      label: "Disponível",
      tone: "success" as const,
      clinicalAccess: false as const,
      note: "Estado administrativo concluído; nenhum link clínico foi emitido.",
    };
  if (status === "ATIVO" && demo.orientationSimulated)
    return {
      label: "Orientação simulada",
      tone: "success" as const,
      clinicalAccess: false as const,
      note: "Orientação marcada como enviada somente nesta sessão; sem envio externo ou acesso clínico.",
    };
  if (status === "ATIVO" && demo.consentSimulated)
    return {
      label: "Aguardando orientação",
      tone: "info" as const,
      clinicalAccess: false as const,
      note: "Consentimento apenas simulado nesta sessão; a orientação ainda não foi simulada.",
    };
  if (status === "ATIVO")
    return {
      label: "Aguardando acesso",
      tone: "info" as const,
      clinicalAccess: false as const,
      note: "Acesso clínico não habilitado nesta demonstração.",
    };
  if (firstAccess.toLocaleLowerCase("pt-BR").includes("consent"))
    return {
      label: "Aguardando consentimento",
      tone: "warning" as const,
      clinicalAccess: false as const,
      note: "Benefício não elegível; permanece sem ativação clínica.",
    };
  return {
    label: "Aguardando dados",
    tone: "warning" as const,
    clinicalAccess: false as const,
    note: "Benefício não elegível; permanece sem ativação clínica.",
  };
}

export function telemedicineListState(count: number) {
  return count === 0 ? ("empty" as const) : ("ready" as const);
}

export function TelemedicinePage() {
  const query = useTelemedicine();
  const [demoSession, setDemoSession] = useState(readTelemedicineDemoSession);
  useEffect(
    () =>
      subscribeDemoReset(() => {
        clearTelemedicineDemoSession();
        setDemoSession({});
      }),
    [],
  );
  if (query.isLoading) return <Skeleton className="table-skeleton" />;
  if (query.error)
    return (
      <div role="alert">
        <EmptyState title="Falha ao carregar telemedicina" description={query.error.message} />
      </div>
    );
  const entries = query.data ?? [];
  const updateDemoSession = (id: string, update: Partial<TelemedicineDemoRecord>) => {
    const next = {
      ...demoSession,
      [id]: {
        ...(demoSession[id] ?? { consentSimulated: false, orientationSimulated: false }),
        ...update,
      },
    };
    writeTelemedicineDemoSession(next);
    setDemoSession(next);
  };
  return (
    <div className="page-stack">
      <section className="page-heading-row">
        <div>
          <p className="eyebrow">OPERAÇÃO · BENEFÍCIOS</p>
          <h2>Telemedicina</h2>
          <p className="muted">
            Acompanhe elegibilidade, requisitos e acesso do beneficiário em linguagem operacional.
          </p>
        </div>
      </section>
      <div className="inline-notice" role="note">
        <CircleAlert size={18} />
        <div>
          <strong>Fluxo em homologação</strong>
          <span>
            Os dados desta tela são fictícios. Os botões abaixo avançam apenas etapas
            administrativas simuladas nesta sessão. Não houve consentimento legal ou real, envio
            externo, emissão de link clínico nem chamada à MobiMedical. A demonstração não habilita
            acesso clínico.
          </span>
        </div>
      </div>
      <section className="metric-grid metric-grid-four" aria-label="Resumo da telemedicina">
        <article className="metric-card">
          <span>Beneficiários</span>
          <strong>{entries.length}</strong>
          <small>Elegibilidade acompanhada</small>
        </article>
        <article className="metric-card">
          <span>Disponíveis</span>
          <strong>
            {
              entries.filter(
                (entry) =>
                  operationalState(entry.status, entry.firstAccess, demoSession[entry.id]).label ===
                  "Disponível",
              ).length
            }
          </strong>
          <small>Estado administrativo concluído</small>
        </article>
        <article className="metric-card">
          <span>Aguardando acesso</span>
          <strong>
            {
              entries.filter(
                (entry) =>
                  operationalState(entry.status, entry.firstAccess, demoSession[entry.id]).label ===
                  "Aguardando acesso",
              ).length
            }
          </strong>
          <small>Próxima ação operacional</small>
        </article>
        <article className="metric-card">
          <span>Em conferência</span>
          <strong>{entries.filter((entry) => entry.status !== "ATIVO").length}</strong>
          <small>Requisitos pendentes</small>
        </article>
      </section>
      <Card
        title="Elegibilidade e acesso"
        description="O operador acompanha o estado e age somente quando há uma próxima etapa válida."
      >
        {telemedicineListState(entries.length) === "empty" ? (
          <div role="status">
            <EmptyState
              title="Nenhum beneficiário em acompanhamento"
              description="Quando houver uma elegibilidade registrada, ela aparecerá nesta lista."
            />
          </div>
        ) : (
          <DataTable
            rows={entries}
            getKey={(entry) => entry.id}
            columns={[
              {
                key: "person",
                header: "Beneficiário",
                render: (entry) => (
                  <div>
                    <strong>{entry.person}</strong>
                    <div className="muted">{entry.family}</div>
                  </div>
                ),
              },
              {
                key: "eligibility",
                header: "Elegibilidade",
                render: (entry) => (
                  <Badge tone={entry.status === "ATIVO" ? "success" : "warning"}>
                    {entry.status === "ATIVO" ? "Elegível" : "Inelegível · sem ativação"}
                  </Badge>
                ),
              },
              {
                key: "access",
                header: "Acesso",
                render: (entry) => {
                  const state = operationalState(
                    entry.status,
                    entry.firstAccess,
                    demoSession[entry.id],
                  );
                  return (
                    <div>
                      <Badge tone={state.tone}>{state.label}</Badge>
                      <div className="muted">{state.note}</div>
                    </div>
                  );
                },
              },
              {
                key: "last",
                header: "Última orientação",
                render: (entry) =>
                  demoSession[entry.id]?.orientationSimulated
                    ? "Orientação simulada (esta sessão)"
                    : entry.firstAccess,
              },
              {
                key: "action",
                header: "Próxima ação",
                render: (entry) => {
                  const state = operationalState(
                    entry.status,
                    entry.firstAccess,
                    demoSession[entry.id],
                  );
                  if (state.label === "Disponível")
                    return (
                      <span className="muted">
                        <CheckCircle2 size={15} /> Estado administrativo concluído (demo)
                      </span>
                    );
                  if (state.label === "Orientação simulada")
                    return (
                      <span className="muted">
                        <CheckCircle2 size={15} /> Orientação concluída somente nesta sessão
                      </span>
                    );
                  if (state.label === "Aguardando orientação" && env.useMocks)
                    return (
                      <Button
                        variant="ghost"
                        onClick={() => updateDemoSession(entry.id, { orientationSimulated: true })}
                        title="Marca somente a etapa de orientação nesta sessão local"
                      >
                        <Send size={15} /> Simular envio da orientação (demo)
                      </Button>
                    );
                  if (state.label === "Aguardando acesso")
                    return (
                      <Button
                        variant="ghost"
                        disabled={!env.useMocks}
                        onClick={() => updateDemoSession(entry.id, { consentSimulated: true })}
                        title="Simula consentimento somente nesta sessão local"
                      >
                        <FileCheck2 size={15} /> Simular consentimento (demo)
                      </Button>
                    );
                  if (state.label === "Aguardando consentimento" && env.useMocks)
                    return (
                      <Button
                        variant="ghost"
                        onClick={() => updateDemoSession(entry.id, { consentSimulated: true })}
                        title="Altera somente o estado desta demonstração local"
                      >
                        <FileCheck2 size={15} /> Simular consentimento (demo)
                      </Button>
                    );
                  if (state.label === "Aguardando consentimento")
                    return (
                      <Button
                        variant="ghost"
                        disabled
                        title="O consentimento persistente depende do fluxo autorizado do titular"
                      >
                        <FileCheck2 size={15} /> Aguardando consentimento do titular
                      </Button>
                    );
                  return (
                    <span className="muted">
                      <Clock3 size={15} /> Aguardar requisitos
                    </span>
                  );
                },
              },
            ]}
          />
        )}
      </Card>
      <Card
        title="Orientação operacional"
        description="Consentimento e requisitos devem ser conduzidos pelo titular no fluxo adequado."
      >
        <div className="three-feature-grid">
          <div className="feature-item">
            <strong>
              <FileCheck2 size={17} /> 1. Conferir dados
            </strong>
            <p>Verifique o cadastro e a elegibilidade antes de solicitar o acesso.</p>
          </div>
          <div className="feature-item">
            <strong>
              <CheckCircle2 size={17} /> 2. Aguardar consentimento
            </strong>
            <p>O operador não confirma consentimento em nome do titular.</p>
          </div>
          <div className="feature-item">
            <strong>
              <Send size={17} /> 3. Orientar o acesso
            </strong>
            <p>Reenviar orientação só aparece após o estado operacional permitir.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
